import React, { createContext, useContext, useState } from 'react';
import getLogger from '@ignitesales/logger-es';
import PropTypes from 'prop-types';
import Base from './QuestionGroups/Base';
import Calculated from './QuestionTypes/Calculated';
import {defaultHtmlFormatValues, useNeuroMessages} from './MessageContext';
import Tooltip, {QuestionAnswersToolTip} from './Tooltip';
import {useBreakpoints} from '../../neuro-ui/lib/BreakpointContext/BreakpointContext';
import {useGuideConfig} from '../hooks/index.mjs';

//TODO: document the hooks values / functions of this provider
const logger = getLogger({ component: 'QuestionContext' });

/**
 * @typedef QuestionContextObject
 * @property {function(obj:Object): string} getQuestionAnswerMessage - returns text for question answer key
 * @property {function(obj:Object): Object[]} getQuestionAnswersArray - returns list of answer options objects for rendering
 * @property {function(type:string): React.Component|null} getQuestionGroupComponent - returns question group component
 * @property {function(type:string): React.Component|null} getQuestionComponent - returns question component
 * @property {function(questionOrAppkey:string|Object): Object|null} getQuestionMods - returns question mod object if found for a question
 * @property {function(question:string, answerKey:string|null=): String} getCustomClasses - returns custom class for question or respective answer
 * @property {function(question:string, answerKey:string|null=, props:Object|null=): React.Component} renderCustomTooltip - returns tooltip for rendering
 * @property {Object} questionGroupTypes - question group values
 * @property {Object} questionTypes - question type values
 */

/**
 * @type QuestionContextObject
 */
const defaultValue = {};
const QuestionContext = createContext(defaultValue);

const QuestionContextProvider = ({
  children,
  questionGroupTypes,
  questionTypes,
}) => {
  const messageContext = useNeuroMessages();
  const { uiConfig = {} } = useGuideConfig();
  const { getCurrentBreakpoint } = useBreakpoints();
  const defaultQuestionGroupComponent = Base;
  const defaultQuestionComponent = Calculated;
  const [displayValidationErrors, setDisplayValidationErrors] = useState(false);
  const questionModConfig = uiConfig && Object.prototype.hasOwnProperty.call(uiConfig, 'questionMods') ? uiConfig.questionMods : {};

  const getQuestionGroupComponent = (type) => {
    const component = questionGroupTypes?.[type];
    if (!component) {
      logger.warn('Question group type not found, using default', type);
    }
    return component || defaultQuestionGroupComponent;
  };

  const getQuestionComponent = (type) => {
    //mm-web specifically looks for lower case 'calculated' type in database...hence:
    const fileName = type.toLowerCase() === 'calculated' ? 'Calculated' : type;
    //search for breakpoint configured component first then fall back to defaults
    const activeBreakpoint = getCurrentBreakpoint();
    const component = questionTypes?.[activeBreakpoint]?.[fileName] || questionTypes?.[fileName];
    if (!component) {
      logger.warn('Question type not found, using default', fileName);
    }
    return component || defaultQuestionComponent;
  };

  const getQuestionAnswerMessage = ({ questionKey, answerKey, customTag = 'span' }) => {
    let messageKey = `${questionKey}.${answerKey}`;
    return messageContext.formatIntlMessage(
      { id: messageKey, tagName: customTag },
      { ...defaultHtmlFormatValues }
    );
  };

  const getQuestionAnswersArray = ({ question, customTag = 'span' }) => {
    return (question.answers || []).map((answer) => {
      return {
        key: `${question.appkey}.${answer.key}`,
        label: getQuestionAnswerMessage({ questionKey: question.appkey, answerKey: answer.key, customTag }),
        value: answer.key,
        subtext: messageContext.getMessage(`${question.appkey}.${answer.key}.subtext`) || '',
        tooltip: messageContext.hasMessage(`${question.appkey}.${answer.key}.tooltip`)
          ? <QuestionAnswersToolTip questionKey={ question.appkey } answerKey={ answer.key } attributes={ answer?.attributes } />
          : null,
        customClasses: getCustomClasses(question, answer.key),
        customTooltip: renderCustomTooltip(question, answer.key)
      };
    });
  };

  const getQuestionMods = questionOrAppkey => {
    const appkey = questionOrAppkey instanceof String ? questionOrAppkey.toString() : questionOrAppkey?.appkey || '';
    if (!appkey) throw 'Invalid question object / no appkey associated';
    return Object.prototype.hasOwnProperty.call(questionModConfig, appkey) ? questionModConfig[appkey] : null;
  };

  const getCustomClasses = (question, answerKey) => {
    const questionMods = getQuestionMods(question);
    if (answerKey && questionMods) {
      logger.debug('Getting custom class for answer', answerKey);
      const answerMods = Object.prototype.hasOwnProperty.call(questionMods?.answers || {}, answerKey) ? questionMods?.answers[answerKey] : {};
      return answerMods?.classes || '';
    } else if (questionMods) {
      logger.debug('Getting custom class for question', question);
      return questionMods?.classes || '';
    } else {
      return '';
    }
  };

  const renderCustomTooltip = (question, answerKey, tooltipProps={}) => {
    const questionMods = getQuestionMods(question);
    let tooltip = {};
    if ((answerKey || answerKey === false) && questionMods) {
      logger.debug('Returning custom tooltip for answer', answerKey);
      const answerMods = Object.prototype.hasOwnProperty.call(questionMods?.answers ||{}, answerKey) ? questionMods?.answers[answerKey] : {};
      tooltip = answerMods?.tooltip;
    } else if (questionMods) {
      logger.debug('Returning custom tooltip for question', question);
      tooltip = questionMods?.tooltip;
    }
    return tooltip?.messageId ? <Tooltip
      messageId={tooltip.messageId}
      className={`${tooltipProps.className || ''} customTooltip`}
      iconRender={tooltip.icon}
      {...tooltipProps}
    /> : null;
  };

  /**
   * @type QuestionContextObject
   */
  const contextValue = {
    getQuestionAnswerMessage,
    getQuestionAnswersArray,
    getQuestionGroupComponent,
    getQuestionComponent,
    getQuestionMods,
    getCustomClasses,
    renderCustomTooltip,
    setDisplayValidationErrors,
    displayValidationErrors,
    questionGroupTypes,
    questionTypes,
  };
  return (
    <QuestionContext.Provider value={contextValue}>
      {children}
    </QuestionContext.Provider>
  );
};

/**
 * @returns QuestionContextObject
 */
const useQuestionContext = () => {
  return useContext(QuestionContext);
};

QuestionContextProvider.propTypes = {
  children: PropTypes.node,
  questionGroupTypes: PropTypes.object.isRequired,
  questionTypes: PropTypes.object.isRequired,
};
export { QuestionContextProvider, useQuestionContext };
